import { AnimatedSprite, BlurFilter, Container, Graphics, type Texture } from 'pixi.js';

// Defaults (can be overridden per-torch via TorchOptions).
const DEFAULT_TORCH_HEIGHT = 90;
const FLAME_SPEED = 0.75;         // AnimatedSprite.animationSpeed
// Halo (warm ground-light around the torch). Renders behind the flame, additive.
// Halo radius is derived from torch height so bigger torches get proportionally
// bigger light - keeps the flame-vs-light ratio consistent across sizes.
const HALO_TO_HEIGHT_RATIO = 70 / 90;
const HALO_COLOR = 0xffaa44;
const HALO_ALPHA_BASE = 0.45;
const HALO_ALPHA_AMP = 0.15;      // sine flicker amplitude on alpha
const HALO_SCALE_AMP = 0.05;      // sine flicker amplitude on scale
const HALO_BLUR = 38;
// Wall-time period - light shouldn't slow down with BATTLE_PACE_SCALE.
const FLICKER_PERIOD_MS = 2500;

export type TorchOptions = {
  frames: Texture[];
  // -1 mirrors the whole container so flame leans the opposite way (e.g. for
  // the right corner). Halo is symmetric - mirroring invisible.
  facing?: 1 | -1;
  // Logical-px height of the flame sprite (longest dim). Halo scales with it.
  height?: number;
};

export class Torch extends Container {
  private readonly halo: Graphics;
  private readonly startTime = performance.now();

  constructor(opts: TorchOptions) {
    super();
    this.scale.x = opts.facing ?? 1;
    const height = opts.height ?? DEFAULT_TORCH_HEIGHT;
    const haloRadius = height * HALO_TO_HEIGHT_RATIO;

    // Halo first -> renders behind the flame. Scales with torch height.
    this.halo = new Graphics()
      .circle(0, 0, haloRadius)
      .fill({ color: HALO_COLOR, alpha: HALO_ALPHA_BASE });
    this.halo.filters = [new BlurFilter({ strength: HALO_BLUR })];
    this.halo.blendMode = 'add';
    this.addChild(this.halo);

    // Flame on top.
    const flame = new AnimatedSprite(opts.frames);
    flame.anchor.set(0.5, 0.5); // top-center: position.y = top of torch
    const firstFrame = opts.frames[0];
    if (firstFrame) {
      const fitScale = height / firstFrame.height;
      flame.scale.set(fitScale);
    }
    flame.animationSpeed = FLAME_SPEED;
    flame.loop = true;
    flame.play();
    this.addChild(flame);

    // onRender fires per frame only while this torch is in the rendered scene
    // and stops automatically on destroy/removal - no manual ticker add/remove
    // or destroyed-guard needed (the pure-animation case from pixijs-ticker).
    this.onRender = () => this.flicker();
  }

  private flicker(): void {
    const elapsed = performance.now() - this.startTime;
    const phase = (elapsed / FLICKER_PERIOD_MS) * Math.PI * 2;
    const sine = Math.sin(phase);
    this.halo.alpha = HALO_ALPHA_BASE + sine * HALO_ALPHA_AMP;
    const scale = 1 + sine * HALO_SCALE_AMP;
    this.halo.scale.set(scale);
  }
}
